import type { PatientProfile } from '@/api/http-client';

export interface MemedPatientPayload {
  idExterno: string;
  nome: string;
  sexo: string;
  cpf?: string;
  data_nascimento?: string;
  telefone?: string;
  email?: string;
  peso?: number;
  altura?: number;
  endereco?: string;
  cidade?: string;
  nome_mae?: string;
  categoriesConditions?: number[];
}

export interface SetMemedPatientResult {
  success: boolean;
  error?: string;
}

function formatDate(date: Date | string | null | undefined): string | undefined {
  if (!date) return undefined;
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return undefined;
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const year = d.getUTCFullYear();
  return `${day}/${month}/${year}`;
}

function formatCpf(cpf: string | null | undefined): string | undefined {
  if (!cpf) return undefined;
  const digits = String(cpf).replace(/\D/g, '');
  return digits || undefined;
}

function formatPhone(phone: string | null | undefined): string | undefined {
  if (!phone) return undefined;
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length < 10) return undefined;
  return digits;
}

function mapGender(gender: string | null | undefined): string {
  if (!gender) return 'Masculino';
  const normalized = String(gender).toLowerCase();
  if (normalized === 'male' || normalized === 'm' || normalized === 'masculino') return 'Masculino';
  if (normalized === 'female' || normalized === 'f' || normalized === 'feminino') return 'Feminino';
  return 'Masculino';
}

export function mapPatientToMemed(patient: PatientProfile): MemedPatientPayload {
  const payload: MemedPatientPayload = {
    idExterno: patient.id,
    nome: patient.name || '',
    sexo: mapGender(patient.gender),
  };

  if (patient.cpf) {
    payload.cpf = formatCpf(patient.cpf);
  }

  if (patient.birthDate) {
    payload.data_nascimento = formatDate(patient.birthDate);
  }

  if (patient.phone) {
    payload.telefone = formatPhone(patient.phone);
  }

  if (patient.email) {
    payload.email = patient.email;
  }

  if (patient.healthProfile) {
    if (patient.healthProfile.weight) {
      payload.peso = patient.healthProfile.weight;
    }
    if (patient.healthProfile.height) {
      payload.altura = patient.healthProfile.height;
    }
  }

  return payload;
}

export function validateMemedPatient(patient: MemedPatientPayload): { valid: boolean; missingFields: string[] } {
  const missingFields: string[] = [];

  if (!patient.idExterno) {
    missingFields.push('idExterno');
  }

  if (!patient.nome || patient.nome.trim() === '') {
    missingFields.push('nome');
  }

  if (!patient.sexo) {
    missingFields.push('sexo');
  }

  return {
    valid: missingFields.length === 0,
    missingFields,
  };
}
